import { useQueryClient } from '@tanstack/react-query';

import type { TypeCheckInOutStatus, TypeCodeState, TypeWorkInfo } from '../../types';
import { axiosInstance } from '~/services/fetchServiceV2';
import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

/** @category useGetCommuteInfo */
export type TypeGetCommuteInfoResponse = {
  code: string;
  message: string;
  data: {
    /**
     * 출근 상태
     * @remarks
     * - START_WORK(공통 - 출근중)
     * - AVAILABLE_LABOR_CONTRACT (일용직 - 계약체결 필요)
     * - AVAILABLE_START_WORK (공통 - 출근 가능)
     * - UNAVAILABLE_START_WORK_BY_INSUFFICIENT_REST (공통 - 조퇴 후 10시간 미만으로 출근 불가능)
     * - UNAVAILABLE_START_WORK_BY_MISSING_WORK_PLAN (상용직 - 근무계획이 등록되지 않았거나, 조출(5시간) 불가 시각이라 출근 불가능)
     * - UNAVAILABLE_START_WORK_BY_DAY_OFF_WORK_PLAN (상용직 - 휴무 근무유형 근무계획으로 출근 불가능)
     * - UNAVAILABLE_START_WORK_BY_TODAY_WORK_PLAN_ENDED (상용직 - 다음 근무계획이 근무시작가능 범위 밖에 있어 출근 불가능)
     * - UNAVAILABLE_START_WORK_BY_TODAY_WORK_END (일용직 - 금일(오늘) 퇴근하여 출근 불가능)
     */
    status: TypeCodeState;
    data: Omit<TypeWorkInfo, 'status' | 'recentCheckInOut' | 'workType'> | null;
    /** 최근 체크인/체크아웃 정보 */
    recentCheckInOut: TypeCheckInOutStatus | null;
    /** 근무 유형 */
    workType: TypeCodeState | null;
  };
};

/**
 * @description 작업자의 현재 출근 및 체크인 정보를 가져오는 api 요청에 사용되는 커스텀 훅
 */
export const useGetCommuteInfo = () => {
  const queryClient = useQueryClient();

  const requestGetCommuteInfo = async ({
    onSuccess,
    onError,
    showLoading = true,
  }: {
    onSuccess: (response: TypeGetCommuteInfoResponse) => void;
    onError?: (error: TypeErrorFormat) => void;
    showLoading?: boolean;
  }) => {
    const endpoint = '/commutes/current';
    const query = queryClient.ensureQueryData({
      queryKey: ['commutes', 'current'],
      queryFn: () => axiosInstance.get(endpoint),
      gcTime: 0,
    });

    return requestHelper(() => query, onSuccess, onError, undefined, { showLoading });
  };

  return requestGetCommuteInfo;
};
